import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "src/components/ui/alert-dialog";
import useHandleLogout from "src/hooks/useHandleLogout";
import type { SessionExpiredReason } from "src/lib/features/auth/auth.type";
import { selectUserState } from "src/lib/features/auth/authSlice";
import { useTypedSelector } from "src/lib/store/store";

/* The session is genuinely gone by the time this renders, so every path out
   leads to the login page -- there is no cancel, no overlay-dismiss, no Esc. */
const COPY: Record<
  SessionExpiredReason,
  { title: string; description: string }
> = {
  idle: {
    title: "Your session has expired",
    description:
      "You've been signed out after a period of inactivity. Please log in again to pick up where you left off.",
  },
  cap: {
    title: "Please log in again",
    description:
      "For your security, sessions are limited in length and yours has now reached that limit. Logging in again will start a fresh session.",
  },
  revoked: {
    title: "You've been signed out",
    description:
      "We ended this session because your sign-in details may have been used somewhere else. If this wasn't you, please log in and change your password.",
  },
  unknown: {
    title: "Your session has ended",
    description: "Please log in again to continue.",
  },
};

export default function SessionExpiredModal() {
  const { sessionExpiredReason } = useTypedSelector(selectUserState);
  const { handleLogout } = useHandleLogout();

  if (!sessionExpiredReason) return null;

  const { title, description } = COPY[sessionExpiredReason];

  return (
    <AlertDialog open>
      <AlertDialogContent
        /* Radix closes on Esc by default, which would leave a dead app
           behind an intact-looking UI. Outside clicks never dismiss an
           AlertDialog (this radix version does not even expose the prop). */
        onEscapeKeyDown={(event) => event.preventDefault()}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter>
          <AlertDialogAction onClick={() => handleLogout()}>
            Log in again
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
